// FEATURE-86 — zero-JS auto-init from `data-ba-*` attributes on the launcher
// `<script>` tag. Lets a low-tech host paste ONE tag and write no JavaScript:
//
//   <script src="http://192.168.50.186/widget.js"
//           data-ba-site-id="customer-uat"
//           data-ba-theme="worldtrans"
//           data-ba-locale="vi" defer></script>
//
// Recognized attributes (all optional except the auto-init trigger):
//   data-ba-api-base   → apiBase            (defaults to the script's own origin)
//   data-ba-site-id    → siteContext.siteId (auto-init trigger)
//   data-ba-page-kind  → siteContext.pageKind
//   data-ba-locale     → locale  ('vi' | 'en' | 'auto')
//   data-ba-theme      → theme   ('default' | 'worldtrans'; unknown → init defaults it)
//   data-ba-user-role  → user.role ('b2c' | 'b2b' | 'corporate' | 'agent')
//   data-ba-position   → position ('bottom-left' default | 'bottom-right')
//   data-ba-margin-x   → margin.x (px from the docked side)
//   data-ba-margin-y   → margin.y (px from the bottom)
//   data-ba-width      → popup default width (px)
//   data-ba-height     → popup default height (px)
//   data-ba-resizable  → resizable ('true' | 'false')
//   data-ba-icon       → launcher icon (image URL or raw <svg> markup)
//   data-ba-launcher-size → launcher button diameter (px)
//   data-ba-hide-launcher → hideLauncher ('true' | 'false')
//   data-ba-accent     → accentColor (any CSS color)
//
// `siteContext.pageUrl` / `pageTitle` are auto-filled from the host page.
// `authToken` is intentionally NOT accepted here — tokens belong in a
// server-driven `BAAssistant.updateUser(...)` (PROTOCOL.md §Security).

import type { InitOptions, WidgetPosition } from '@/domain';
import type { RequestLocale, ThemeName, UserHint } from '@ba/protocol';
import { THEME_NAMES } from '@ba/protocol';

import { getAssistantOrigin, getLauncherScript } from './scriptOrigin';

const LOCALES: readonly RequestLocale[] = ['vi', 'en', 'auto'];
const ROLES: readonly NonNullable<UserHint['role']>[] = ['b2c', 'b2b', 'corporate', 'agent'];
const POSITIONS: readonly WidgetPosition[] = ['bottom-left', 'bottom-right'];

function parseLocale(raw: string | undefined): RequestLocale | undefined {
  return raw !== undefined && (LOCALES as readonly string[]).includes(raw)
    ? (raw as RequestLocale)
    : undefined;
}

function parseTheme(raw: string | undefined): ThemeName | undefined {
  // Unknown values are left undefined so `init()`'s resolveTheme() applies the
  // default + console.warn — one place owns the fallback.
  return raw !== undefined && (THEME_NAMES as readonly string[]).includes(raw)
    ? (raw as ThemeName)
    : undefined;
}

function parsePosition(raw: string | undefined): WidgetPosition | undefined {
  return raw !== undefined && (POSITIONS as readonly string[]).includes(raw)
    ? (raw as WidgetPosition)
    : undefined;
}

function parseUser(raw: string | undefined): UserHint | undefined {
  return raw !== undefined && (ROLES as readonly string[]).includes(raw)
    ? { role: raw as NonNullable<UserHint['role']> }
    : undefined;
}

/** Parse a positive integer px attribute; undefined when absent/invalid. */
function parsePx(raw: string | undefined): number | undefined {
  if (raw === undefined || raw === '') return undefined;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? n : undefined;
}

/** Parse a boolean attribute (`"true"`/`"false"`); undefined when absent. */
function parseBool(raw: string | undefined): boolean | undefined {
  if (raw === 'true') return true;
  if (raw === 'false') return false;
  return undefined;
}

/** Build the optional `{ x, y }` margin from the two attrs (undefined if neither). */
function parseMargin(
  x: string | undefined,
  y: string | undefined,
): InitOptions['margin'] {
  const px = parsePx(x);
  const py = parsePx(y);
  if (px === undefined && py === undefined) return undefined;
  return { x: px, y: py };
}

export interface ScriptConfigResult {
  options: InitOptions;
}

/**
 * Read auto-init options off the launcher `<script>` tag, or `null` when the
 * tag carries no `data-ba-*` trigger (then the host is expected to call
 * `BAAssistant.init(...)` manually). Presence of `data-ba-site-id` OR
 * `data-ba-api-base` is the trigger.
 */
export function readScriptConfig(
  win: Window = window,
  doc: Document = document,
): ScriptConfigResult | null {
  const script = getLauncherScript();
  if (script === null) return null;

  const d = script.dataset;
  const siteId = d.baSiteId;
  const apiBaseAttr = d.baApiBase;

  const hasSiteId = siteId !== undefined && siteId !== '';
  const hasApiBase = apiBaseAttr !== undefined && apiBaseAttr !== '';
  if (!hasSiteId && !hasApiBase) return null;

  // apiBase: explicit attr → derived assistant origin → '/api' (same-origin to
  // the panel, which normalizes to '' in init()). Never '' here — init()'s
  // normalizeApiBase() rejects an empty string.
  const apiBase = hasApiBase ? (apiBaseAttr as string) : (getAssistantOrigin() ?? '/api');

  const options: InitOptions = {
    apiBase,
    siteContext: {
      siteId: hasSiteId ? (siteId as string) : 'embedded',
      pageUrl: win.location.href,
      pageTitle: doc.title,
      pageKind: d.baPageKind,
    },
    locale: parseLocale(d.baLocale),
    theme: parseTheme(d.baTheme),
    user: parseUser(d.baUserRole),
    position: parsePosition(d.baPosition),
    margin: parseMargin(d.baMarginX, d.baMarginY),
    width: parsePx(d.baWidth),
    height: parsePx(d.baHeight),
    resizable: parseBool(d.baResizable),
    icon: d.baIcon !== '' ? d.baIcon : undefined,
    launcherSize: parsePx(d.baLauncherSize),
    hideLauncher: parseBool(d.baHideLauncher),
    accentColor: d.baAccent !== '' ? d.baAccent : undefined,
  };
  return { options };
}
